import React, { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCheck,
  FaCreditCard,
  FaExclamationTriangle,
  FaPauseCircle,
  FaSpinner,
} from "react-icons/fa";
import HeaderComponent from "../../Header";
import useBilling from "../hooks/useBilling";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~2 minutes

const PENDING_STATUSES = ["pending_approval", "approved"];

const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

const Banner = ({ tone, icon: Icon, title, children }) => {
  const tones = {
    warning: "bg-yellow-50 border-yellow-300 text-yellow-800",
    danger: "bg-red-50 border-red-300 text-red-800",
    info: "bg-blue-50 border-blue-300 text-blue-800",
  };

  return (
    <div className={`border rounded-lg p-4 mb-6 flex items-start ${tones[tone]}`}>
      {Icon && <Icon className="mt-1 mr-3 flex-shrink-0" />}
      <div>
        <p className="font-semibold">{title}</p>
        {children && <p className="text-sm mt-1">{children}</p>}
      </div>
    </div>
  );
};

const UsageMeter = ({ usage }) => {
  const limit = usage && usage.limit ? usage.limit : 0;
  const used = (usage && usage.used) || 0;
  const remaining = (usage && usage.remaining) || 0;
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const exhausted = remaining === 0;

  return (
    <div className="mt-6">
      <div className="flex justify-between text-sm text-gray-600 mb-2">
        <span>
          {used} of {limit} certificates used this period
        </span>
        <span className={exhausted ? "font-semibold text-red-600" : "font-semibold text-gray-800"}>
          {remaining} remaining
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div
          className={`h-3 rounded-full ${exhausted ? "bg-red-500" : "bg-blue-600"}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {exhausted && (
        <p className="text-sm text-red-600 mt-2">
          Your allowance resets when your subscription renews.
        </p>
      )}
    </div>
  );
};

const StatusBadge = ({ label, tone }) => {
  const tones = {
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
    gray: "bg-gray-100 text-gray-800",
  };

  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full ${tones[tone]}`}>{label}</span>
  );
};

const PlanFeatures = ({ plan }) => (
  <ul className="mt-6 space-y-3 text-gray-700">
    <li className="flex items-start">
      <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
      Generate up to {plan.certificate_limit} certificates per {plan.interval}
    </li>
    <li className="flex items-start">
      <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
      Bulk CSV import and email delivery
    </li>
    <li className="flex items-start">
      <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
      Cancel anytime — you keep access until the end of the period you paid for
    </li>
  </ul>
);

const SubscribeButton = ({ label, loading, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="mt-8 w-full flex items-center justify-center py-3 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
  >
    {loading ? <FaSpinner className="animate-spin mr-2" /> : <FaCreditCard className="mr-2" />}
    {loading ? "Starting checkout…" : label}
  </button>
);

const App = (props) => {
  const { billing, loading, subscribe, cancel, fetchStatus } = useBilling(props.billing || null);
  const [polling, setPolling] = useState(false);
  const cancelledNoticeShown = useRef(false);

  const status = (billing && billing.status) || "none";
  const plan = billing && billing.plan;
  const usage = billing && billing.usage;
  const enforcement = !!(billing && billing.enforcement);
  const paidThrough = !!(billing && billing.paid_through);
  const periodEnd = formatDate(billing && billing.current_period_end);

  // "Checkout cancelled" arrives as ?cancelled=1 on the PayPal cancel_url.
  useEffect(() => {
    if (cancelledNoticeShown.current) return;
    if (!window.location.search.includes("cancelled=1")) return;

    cancelledNoticeShown.current = true;
    toast.info("Checkout cancelled — you have not been charged.", {
      position: "bottom-center",
      autoClose: 5000,
    });
  }, []);

  // While PayPal finishes activating the subscription, poll until it lands.
  useEffect(() => {
    if (!PENDING_STATUSES.includes(status)) {
      setPolling(false);
      return undefined;
    }

    setPolling(true);
    let attempts = 0;

    const timer = window.setInterval(async () => {
      attempts += 1;
      const fresh = await fetchStatus();

      if (attempts >= POLL_MAX_ATTEMPTS || (fresh && !PENDING_STATUSES.includes(fresh.status))) {
        window.clearInterval(timer);
        setPolling(false);
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const renderPlanCard = (label) => (
    <>
      <div className="flex items-baseline justify-between">
        <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
        <p className="text-3xl font-bold text-gray-900">
          {plan.price}
          <span className="text-base font-normal text-gray-500">/{plan.interval}</span>
        </p>
      </div>
      <PlanFeatures plan={plan} />
      <SubscribeButton label={label} loading={loading} onClick={subscribe} />
      <p className="mt-3 text-xs text-center text-gray-500">
        You will be taken to PayPal to approve the subscription.
      </p>
    </>
  );

  const renderBody = () => {
    if (!plan) {
      return (
        <div className="text-center py-6">
          <h2 className="text-xl font-semibold text-gray-800">Subscriptions are not available yet</h2>
          <p className="text-gray-500 mt-2">Please check back shortly.</p>
        </div>
      );
    }

    if (PENDING_STATUSES.includes(status)) {
      return (
        <div className="text-center py-10">
          <FaSpinner className="animate-spin mx-auto text-3xl text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-800 mt-4">Finishing setup with PayPal…</h2>
          <p className="text-gray-500 mt-2">
            This usually takes a few seconds. {polling ? "Checking for updates…" : ""}
          </p>
          <button
            type="button"
            onClick={fetchStatus}
            className="mt-6 text-blue-600 hover:underline text-sm"
          >
            Check now
          </button>
        </div>
      );
    }

    if (status === "active" || status === "past_due") {
      return (
        <>
          {status === "past_due" && (
            <Banner tone="warning" icon={FaExclamationTriangle} title="Payment failed">
              PayPal will retry automatically. Update your payment method in your PayPal account to
              avoid losing access.
            </Banner>
          )}

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
              <p className="text-gray-500 mt-1">
                {plan.price}/{plan.interval}
                {periodEnd ? ` — renews on ${periodEnd}` : ""}
              </p>
            </div>
            <StatusBadge
              label={status === "active" ? "Active" : "Past due"}
              tone={status === "active" ? "green" : "yellow"}
            />
          </div>

          <UsageMeter usage={usage} />

          <button
            type="button"
            onClick={cancel}
            disabled={loading}
            className="mt-8 w-full py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-60 transition"
          >
            Cancel subscription
          </button>
        </>
      );
    }

    if (status === "suspended") {
      return (
        <>
          <Banner tone="danger" icon={FaPauseCircle} title="Subscription suspended">
            Certificate generation is paused. Start a new subscription to continue.
          </Banner>
          <UsageMeter usage={usage} />
          {renderPlanCard("Resubscribe with PayPal")}
        </>
      );
    }

    if (status === "cancelled" && paidThrough) {
      return (
        <>
          <Banner tone="info" icon={FaExclamationTriangle} title="Subscription cancelled">
            You keep access until {periodEnd || "the end of your paid period"}.
          </Banner>
          <UsageMeter usage={usage} />
          {renderPlanCard("Resubscribe with PayPal")}
        </>
      );
    }

    // none, expired, or cancelled past the paid period.
    return renderPlanCard(status === "none" ? "Subscribe with PayPal" : "Resubscribe with PayPal");
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <HeaderComponent />
      <div className="container mx-auto py-8 px-4 max-w-2xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Billing</h1>

        {!enforcement && (
          <Banner tone="info" title="Early access — billing is not yet enforced">
            Certificate generation is not limited yet. You can subscribe now to be ready when it is.
          </Banner>
        )}

        <div className="bg-white shadow rounded-lg p-8">{renderBody()}</div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default App;
